import { useCallback, useState } from 'react';
import { Button } from '@/shared/ui/Button';
import { Toast, type TipoToast } from '@/shared/ui/Toast';
import { usePosVendaEntitlement } from '@/features/pos-venda/hooks/usePosVendaEntitlement';
import { useAvaliacaoOS } from '@/features/pos-venda/hooks/useAvaliacaoOS';
import type { AvaliacaoOS } from '@/features/pos-venda/api/avaliacoesApi';
import { EstrelaNota, EstrelasLinha } from '@/features/pos-venda/components/EstrelaNota';

/** Nota abaixo disto conta como "ruim" no tom do card (o limite real fica no relatório). */
const LIMITE_RUIM_PADRAO = 3;

const formatadorDataHora = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatarDataHora(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : formatadorDataHora.format(d).replace(',', ' às');
}

type Props = {
  orcamentoId: string;
  clienteNome: string;
};

/**
 * Card "Avaliação do cliente" dentro da O.S. Estados:
 *  - respondida: a avaliação completa no próprio card (estrela com a nota,
 *    5 estrelas, data da resposta e o comentário inteiro) — sem modal;
 *  - link gerado, sem resposta: "Aguardando" + copiar link;
 *  - sem link: botão de gerar (só com pós-venda ativo);
 *  - sem conexão: aviso discreto — nunca quebra a tela da O.S.
 * Sem pós-venda e sem avaliação, não aparece.
 */
export function CardAvaliacaoOS({ orcamentoId, clienteNome }: Props) {
  const { avaliacao, carregando, semConexao, erro, recarregar, gerarLink } =
    useAvaliacaoOS(orcamentoId);
  const { ativo: posVendaAtivo, carregando: carregandoEntitlement } = usePosVendaEntitlement();
  const [gerando, setGerando] = useState(false);
  const [toast, setToast] = useState<{ mensagem: string; tipo: TipoToast } | null>(null);
  const fecharToast = useCallback(() => setToast(null), []);

  async function gerarECopiar() {
    setGerando(true);
    try {
      const link = await gerarLink();
      try {
        await navigator.clipboard.writeText(link);
        setToast({ mensagem: 'Link copiado', tipo: 'sucesso' });
      } catch {
        // Sem permissão de área de transferência: mostra o link para copiar à mão.
        setToast({ mensagem: `Link de avaliação: ${link}`, tipo: 'info' });
      }
    } catch (e) {
      setToast({
        mensagem: e instanceof Error ? e.message : 'Falha ao gerar o link de avaliação.',
        tipo: 'erro',
      });
    } finally {
      setGerando(false);
    }
  }

  if (carregando || carregandoEntitlement) return null;
  // Pós-venda inativo e nada para mostrar: o card não existe.
  if (!avaliacao && !posVendaAtivo) return null;

  const respondida = avaliacao?.nota != null;
  const ruim = respondida && (avaliacao?.nota ?? 0) < LIMITE_RUIM_PADRAO;
  const tom = respondida ? (ruim ? 'avaliacao-os--ruim' : 'avaliacao-os--boa') : '';

  return (
    <div className={`card avaliacao-os ${tom}`}>
      <div className="card__header">
        <span className="card__title">Avaliação do cliente</span>
        {semConexao && avaliacao && (
          <span className="hint avaliacao-os__offline-tag">Sem conexão — último valor conhecido</span>
        )}
      </div>
      <div className="card__body">
        {respondida && avaliacao ? (
          <Respondida
            avaliacao={avaliacao as AvaliacaoOS & { nota: number }}
            ruim={ruim}
            clienteNome={clienteNome}
          />
        ) : semConexao ? (
          <p className="hint avaliacao-os__discreto">Avaliação indisponível sem conexão.</p>
        ) : erro && !avaliacao ? (
          <div className="avaliacao-os__linha">
            <p className="hint avaliacao-os__discreto">Não foi possível carregar a avaliação.</p>
            <Button variante="ghost" onClick={() => void recarregar()}>
              Tentar de novo
            </Button>
          </div>
        ) : avaliacao ? (
          <div className="avaliacao-os__linha">
            <div>
              <strong>Aguardando avaliação do cliente</strong>
              <div className="hint avaliacao-os__discreto">
                Link gerado em {formatarDataHora(avaliacao.criadoEm)}
              </div>
            </div>
            <Button onClick={() => void gerarECopiar()} disabled={gerando}>
              {gerando ? 'Copiando…' : 'Copiar link de avaliação'}
            </Button>
          </div>
        ) : (
          <div className="avaliacao-os__linha">
            <p className="hint avaliacao-os__discreto">
              Peça ao cliente uma nota de 1 a 5 estrelas para este serviço.
            </p>
            <Button variante="primary" onClick={() => void gerarECopiar()} disabled={gerando}>
              {gerando ? 'Gerando…' : 'Gerar link de avaliação'}
            </Button>
          </div>
        )}
      </div>

      <Toast mensagem={toast?.mensagem ?? null} tipo={toast?.tipo} onFechar={fecharToast} />
    </div>
  );
}

function Respondida({
  avaliacao,
  ruim,
  clienteNome,
}: {
  avaliacao: AvaliacaoOS & { nota: number };
  ruim: boolean;
  clienteNome: string;
}) {
  const comentario = avaliacao.comentario?.trim();
  return (
    <div className="avaliacao-os__respondida">
      <div className="avaliacao-os__cabeca">
        <span className="avaliacao-os__estrela" role="img" aria-label={`Nota ${avaliacao.nota} de 5`}>
          <EstrelaNota nota={avaliacao.nota} tamanho={56} />
        </span>
        <div className="avaliacao-os__resumo">
          <strong className="avaliacao-os__rotulo">{ruim ? 'Avaliação ruim' : 'Boa avaliação'}</strong>
          <span className="avaliacao-os__nota">
            <EstrelasLinha nota={avaliacao.nota} tamanho={18} />
            <span>
              <strong>{avaliacao.nota}</strong> de 5
            </span>
          </span>
          {avaliacao.respondidoEm && (
            <span className="hint avaliacao-os__discreto">
              Respondida em {formatarDataHora(avaliacao.respondidoEm)}
            </span>
          )}
        </div>
      </div>
      {comentario ? (
        <figure className="avaliacao-os__comentario">
          <blockquote>{comentario}</blockquote>
          {clienteNome && <figcaption className="hint">— {clienteNome}</figcaption>}
        </figure>
      ) : (
        <p className="hint avaliacao-os__discreto avaliacao-os__sem-comentario">
          Cliente não deixou comentário
        </p>
      )}
    </div>
  );
}
